import { CSSResultGroup, LitElement, TemplateResult, css, html } from 'lit';
import { GoogleTagEvent, logWithAmplitude, logWithGoogleTagAndTopicOrganization } from '../../helpers/api/api';
import { customElement, query } from 'lit/decorators';

import { GetPointResponse } from '../../types';
import { StateController } from '../../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import { debounce } from '../../helpers/utils';
import pointCache from '../../helpers/state/point-cache';
import { pointSearch } from '../../helpers/filters';
import topicData from '../../helpers/state/topic-data';

@customElement('admin-points-list-fragment')
export class AdminPointsListFragment extends LitElement {
    static amplitude_callsite: string = 'Admin Point List Drawer';

    @query('#search')
    search!: TextField;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);

    protected render(): TemplateResult {
        return html`
            <div id="container">
                <proxi-textfield
                    id="search"
                    class="input"
                    outlined
                    label="Search"
                    @input="${this.searchChanged}">
                </proxi-textfield>
                <paper-tooltip for="search" offset="0">
                    Use this search field to filter by category, city, state, etc.
                </paper-tooltip>
                <br />
                <point-list
                    id="points"
                >
                </point-list>
            </div>
            <div class="addon__footer">
                <form-button class="save button" size="xs" variant="outline" @click="${() => this.onDownloadCsv(true)}" raised>
                    Download All ${topicData.topic?.point_name ?? 'Point'}s
                </form-button>
                <form-button class="save button" size="xs" @click="${() => this.onDownloadCsv(false)}" raised>
                    Download Filtered ${topicData.topic?.point_name ?? 'Point'}s
                </form-button>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles(): CSSResultGroup | undefined {
        return css`
            #container {
                flex-direction: column;
                max-height: calc(100vh - 162px);
                overflow: hidden auto;
                margin-bottom: 1rem;
                padding-right: 30px;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            #points {
                flex: 1;
                display: flex;
                overflow-x: hidden;
                overflow-y: auto;
                margin-top: 1rem;
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 5px;
                color: var(--theme-input-color);
            }

            .addon__footer {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                flex-wrap: wrap;
                padding: 0 20px;
                gap: 10px;
            }

            .input {
                width: 100%;
                padding: auto 5px;
            }

            .button {
                display: inline-block;
                color: var(--theme-white-color);
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            @media only screen and (max-width: 550px) {
                .helpHeader {
                    margin: 0.2em 0;
                }
            }
        `;
    }

    private readonly searchChanged = debounce(this.searchChangedInternal, 500);
    private searchChangedInternal() {
        const search = this.search.value?.trim().toLocaleLowerCase() ?? '';

        logWithAmplitude(AdminPointsListFragment.amplitude_callsite, 'Search', { search });

        pointCache.removeFilter('ListSearch');
        if (search !== '') {
            pointCache.filterMarkers({ name: 'ListSearch', filterFn: (p: GetPointResponse) => pointSearch(p, search) });
            logWithGoogleTagAndTopicOrganization(AdminPointsListFragment.amplitude_callsite, GoogleTagEvent.MAP_SEARCH, {
                search_term: search,
            });
        }
    }

    private onDownloadCsv(allPoints: boolean = false) {
        pointCache.onDownloadCsv(allPoints);
    }
}
